/**
 * 10.6L — MUROS E PORTAS ENTRE TERRITÓRIOS.
 *
 * A muralha de 10.6K fecha a BORDA da região. Faltava o que o Thiago pediu: cada território
 * fechado por muro, com uma PORTA que só libera depois de conquistado. Sem isso dava para
 * atravessar 51 pedaços a pé sem nada barrar.
 *
 * Cada linha interna da grade vira um muro instanciado com um VÃO no meio — a porta. A porta
 * tem um painel de energia que:
 *   · VERMELHO e sólido = trancado (empurra o Caçador de volta);
 *   · VERDE e transparente = liberado (atravessa).
 * Em cima do painel trancado fica o contador de abates que falta para conquistar o pedaço,
 * então a regra se aprende olhando a porta, sem menu.
 *
 * Custo: 2 draw calls para todos os muros da região (blocos + capitéis) mais 1 por painel
 * de porta visível. O bloqueio é geométrico (retângulo do vão), não usa o grid de colisão.
 */
import * as THREE from "three";
import { GameConfig, type TerritoryDef } from "../config/GameConfig";
import { territoriesForRegion, regionHalfSize, type Territory } from "./Territories";
import type { TerritoryProgress } from "../progression/TerritoryProgress";
import { crystalStdMat, energyMat, stoneMat, woodMat } from "./Materials";
import { DIVIDE_BANK_COLOR, DIVIDE_HALF, DIVIDE_TYPES, divideMaterials, divideTypeFor, tickDivides, type DivideType } from "./Divides";

/** 10.9HN — segmento de divisa pro mapa/minimapa. */
export interface MapDivide { x0: number; z0: number; x1: number; z1: number; type: DivideType; a: string; b: string }

/** Uma passagem entre dois territórios vizinhos. */
interface Gate {
  x: number;
  z: number;
  /** true = o vão é atravessado no eixo X (muro corre em Z) */
  alongX: boolean;
  half: number; // meia-largura do vão
  a: TerritoryDef; // território de um lado
  b: TerritoryDef; // território do outro lado
  /** índice no InstancedMesh dos painéis. */
  idx: number;
  open: boolean;
}

const _m = new THREE.Object3D();
const _c = new THREE.Color();
const WALL_H = 4.6;
const GATE_HALF = 3.4; // meia-largura do vão (o Caçador tem 0,5 de raio: passa folgado)

export class TerritoryGates {
  readonly group = new THREE.Group();
  private readonly built = new Map<string, THREE.Group>();
  private gates: Gate[] = [];
  private regionId = "";
  private terrs: Territory[] = [];

  /** 10.6L — TODOS os painéis num só InstancedMesh: 22 portas custavam 22 draw calls.
   *  Com blending aditivo, a cor da instância também é a opacidade: vermelho = trancada,
   *  quase preto = liberada (some sem precisar de material separado). */
  private readonly panelGeo = new THREE.PlaneGeometry(1, 1);
  // 10.9BQ — biblioteca de materiais. O painel é CLONE (a opacidade pulsa no update); a cor
  // por porta vem do instanceColor (base branca), então cristais/runa podem ser compartilhados.
  private readonly panelMat = (() => { const m = energyMat(0xffffff, "disc").clone(); m.opacity = 0.85; return m; })(); // 10.8B: trancado = barreira sólida
  private readonly panels = new Map<string, THREE.InstancedMesh>();
  /** 10.6X — ESTRUTURA da porta: pilares + arco + cristais. Faz parecer um portal, não laser. */
  private readonly postGeo = new THREE.BoxGeometry(1, 1, 1);
  private readonly postMat = crystalStdMat(0x6a7290, 0x2e3555, 0.35);
  private readonly beamGeo = new THREE.BoxGeometry(1, 1, 1);
  private readonly beamMat = crystalStdMat(0x4a5470, 0x3a2a5a, 0.4);
  private readonly crystalGeo = new THREE.OctahedronGeometry(0.42, 0);
  private readonly crystalMat = crystalStdMat(0xffffff, 0xffffff, 1.0);
  private readonly runeGeo = new THREE.RingGeometry(1.4, 2.0, 24);
  private readonly runeMat = energyMat(0xffffff, "ring");
  private readonly crystals = new Map<string, THREE.InstancedMesh>();
  private readonly runes = new Map<string, THREE.InstancedMesh>();
  private time = 0;
  /** 10.7V — contexto de desbloqueio: nível do Caçador e Profundidade (trava de nível nas portas). */
  private unlockLevel = 1;
  private unlockDepth = 1;
  /** 10.8B — última posição vista pelo block() (colisão varrida anti-tunelamento). */
  private lastX = NaN;
  private lastZ = NaN;
  /** 10.6N — linhas de muro da região (para a colisão geométrica). */
  private readonly linesByRegion = new Map<string, { x: number[]; z: number[] }>();
  private lines: { x: number[]; z: number[] } = { x: [], z: [] };

  constructor(scene: THREE.Scene, private readonly progress: TerritoryProgress) {
    scene.add(this.group);
  }

  /** Constrói (na primeira visita) e mostra os muros da região. */
  setRegion(id: string): void {
    if (id === this.regionId && this.built.has(id)) return;
    this.regionId = id;
    if (!this.built.has(id)) this.build(id);
    for (const [rid, g] of this.built) g.visible = rid === id;
    this.terrs = territoriesForRegion(id);
    this.gates = this.gatesByRegion.get(id) ?? [];
    this.lines = this.linesByRegion.get(id) ?? { x: [], z: [] };
    this.lastX = NaN; // troca de região = teleporte: zera o sweep p/ não falsar cruzamento
    this.lastZ = NaN;
    _activeGates = this;
    this.refresh();
  }

  private readonly gatesByRegion = new Map<string, Gate[]>();
  /** 10.9HN — divisas (rio/lava/gelo/abismo) por região, pro mapa/minimapa. */
  private readonly divideSegsByRegion = new Map<string, MapDivide[]>();
  mapDivides(): MapDivide[] { return this.divideSegsByRegion.get(this.regionId) ?? []; }

  private build(regionId: string): void {
    const root = new THREE.Group();
    const terrs = territoriesForRegion(regionId);
    const half = regionHalfSize(regionId);
    const gates: Gate[] = [];
    if (terrs.length === 0) {
      this.built.set(regionId, root);
      this.gatesByRegion.set(regionId, gates);
      this.group.add(root);
      return;
    }

    // 10.6M — o progresso precisa da geometria para saber quais têm baú dentro
    for (const t of terrs) this.progress.setGeometry(t.def.id, t.cx, t.cz, t.halfW, t.halfH);

    // 1) descobre as passagens: todo par de vizinhos ortogonais tem UMA porta no meio da divisa
    for (const t of terrs) {
      for (const n of terrs) {
        if (n === t) continue;
        const dc = n.def.col - t.def.col;
        const dr = n.def.row - t.def.row;
        if (dc === 1 && dr === 0) {
          gates.push({ x: t.cx + t.halfW, z: t.cz, alongX: true, half: GATE_HALF, a: t.def, b: n.def, idx: 0, open: true });
        } else if (dr === 1 && dc === 0) {
          gates.push({ x: t.cx, z: t.cz + t.halfH, alongX: false, half: GATE_HALF, a: t.def, b: n.def, idx: 0, open: true });
        }
      }
    }

    // 2) 10.9HN — DIVISAS NATURAIS (rio / lava / gelo / abismo) no lugar dos muros; a colisão
    // continua geométrica nas linhas da grade (block/blockStatic), com a largura da faixa.
    const linesX = new Set<number>();
    const linesZ = new Set<number>();
    for (const t of terrs) {
      linesX.add(+(t.cx + t.halfW).toFixed(2));
      linesX.add(+(t.cx - t.halfW).toFixed(2));
      linesZ.add(+(t.cz + t.halfH).toFixed(2));
      linesZ.add(+(t.cz - t.halfH).toFixed(2));
    }
    const inner = (v: number): boolean => Math.abs(Math.abs(v) - half) > 1.5; // a borda já tem a muralha
    // 10.6Q — margem da Sanctuary: não coloca muro dentro do raio + folga
    const rDef = GameConfig.world.regions.find((r) => r.id === regionId);
    const sanctX = rDef?.sanctuary.center.x ?? 0;
    const sanctZ = rDef?.sanctuary.center.z ?? 0;
    const sanctR2 = ((rDef?.sanctuary.radius ?? GameConfig.sanctuary.radius) + 4) ** 2; // 10.9BP: sem número mágico
    const inSanct = (bx: number, bz: number): boolean => (bx - sanctX) ** 2 + (bz - sanctZ) ** 2 < sanctR2;
    const SEG = 20;
    const mats = divideMaterials();
    const segs: { x: number; z: number; alongX: boolean; type: DivideType; a: string; b: string }[] = [];
    const terrAt = (x: number, z: number): Territory | null => terrs.find((t) => Math.abs(x - t.cx) <= t.halfW && Math.abs(z - t.cz) <= t.halfH) ?? null;
    for (const lx of linesX) {
      if (!inner(lx)) continue;
      for (let z = -half + SEG / 2; z < half; z += SEG) {
        if (inSanct(lx, z)) continue;
        const A = terrAt(lx - 2.5, z), B = terrAt(lx + 2.5, z);
        segs.push({ x: lx, z, alongX: false, type: divideTypeFor(A?.def.biome ?? null, B?.def.biome ?? null, regionId), a: A?.def.id ?? "", b: B?.def.id ?? "" });
      }
    }
    for (const lz of linesZ) {
      if (!inner(lz)) continue;
      for (let x = -half + SEG / 2; x < half; x += SEG) {
        if (inSanct(x, lz)) continue;
        const A = terrAt(x, lz - 2.5), B = terrAt(x, lz + 2.5);
        segs.push({ x, z: lz, alongX: true, type: divideTypeFor(A?.def.biome ?? null, B?.def.biome ?? null, regionId), a: A?.def.id ?? "", b: B?.def.id ?? "" });
      }
    }
    const planeGeo = new THREE.PlaneGeometry(1, 1);
    for (const type of DIVIDE_TYPES) {
      const list = segs.filter((sg) => sg.type === type);
      if (!list.length) continue;
      const im = new THREE.InstancedMesh(planeGeo, mats[type], list.length);
      list.forEach((sg, k) => {
        _m.position.set(sg.x, 0.06, sg.z);
        _m.rotation.set(-Math.PI / 2, 0, sg.alongX ? 0 : Math.PI / 2);
        _m.scale.set(SEG + 0.6, DIVIDE_HALF * 2, 1);
        _m.updateMatrix();
        im.setMatrixAt(k, _m.matrix);
      });
      im.instanceMatrix.needsUpdate = true;
      im.frustumCulled = true;
      im.computeBoundingSphere();
      im.renderOrder = 1;
      root.add(im);
    }
    // 10.9HQ — CRUZAMENTOS (Thiago: "onde os rios se encontram, um passava por cima do outro"). As faixas
    // são retângulos com margem que some (uv.y): no cruzamento a margem de um rio cortava o outro. Um
    // REMENDO quadrado com uv.y fixo em 0.5 (fundo do rio, sem margem) cobre a interseção por cima
    // (renderOrder 2) e as quatro faixas viram um encontro de rios. Tipo = o "mais forte" dos que se cruzam.
    // O remendo é uma grade 12×12 cujo uv.y por vértice reproduz o PERFIL das duas faixas (união em "+"):
    // t = max(1 - |dz|/HALF, 1 - |dx|/HALF) → fundo no meio dos braços, margem/espuma nos cantos, e os
    // cantos do quadrado (fora do "+") somem — nada de placa quadrada por cima do cruzamento.
    const JS = DIVIDE_HALF * 2 + 0.5;
    const junctionGeo = new THREE.PlaneGeometry(1, 1, 12, 12);
    {
      const pos = junctionGeo.attributes.position as THREE.BufferAttribute;
      const uv = junctionGeo.attributes.uv as THREE.BufferAttribute;
      for (let i = 0; i < uv.count; i++) {
        const dx = Math.abs(pos.getX(i)) * JS, dz = Math.abs(pos.getY(i)) * JS;
        const t = Math.max(Math.max(0, 1 - dz / DIVIDE_HALF), Math.max(0, 1 - dx / DIVIDE_HALF));
        uv.setY(i, 0.5 + 0.5 * (1 - t));
      }
      uv.needsUpdate = true;
    }
    const PRI: Record<DivideType, number> = { lava: 3, chasm: 2, ice: 1, water: 0 };
    const junctions: { x: number; z: number; type: DivideType }[] = [];
    for (const lx of linesX) {
      if (!inner(lx)) continue;
      for (const lz of linesZ) {
        if (!inner(lz) || inSanct(lx, lz)) continue;
        const here = segs.filter((sg) => sg.alongX
          ? Math.abs(sg.z - lz) < 0.01 && Math.abs(sg.x - lx) <= SEG / 2 + 0.01
          : Math.abs(sg.x - lx) < 0.01 && Math.abs(sg.z - lz) <= SEG / 2 + 0.01);
        if (here.length < 2) continue; // precisa de faixa nos DOIS eixos pra ser cruzamento
        let type = here[0].type;
        for (const sg of here) if (PRI[sg.type] > PRI[type]) type = sg.type;
        junctions.push({ x: lx, z: lz, type });
      }
    }
    for (const type of DIVIDE_TYPES) {
      const list = junctions.filter((j) => j.type === type);
      if (!list.length) continue;
      const im = new THREE.InstancedMesh(junctionGeo, mats[type], list.length);
      list.forEach((j, k) => {
        _m.position.set(j.x, 0.07, j.z);
        _m.rotation.set(-Math.PI / 2, 0, 0);
        _m.scale.set(JS, JS, 1);
        _m.updateMatrix();
        im.setMatrixAt(k, _m.matrix);
      });
      im.instanceMatrix.needsUpdate = true;
      im.frustumCulled = true;
      im.computeBoundingSphere();
      im.renderOrder = 2;
      root.add(im);
    }
    const nearCrossing = (x: number, z: number): boolean => junctions.some((j) => Math.abs(j.x - x) < DIVIDE_HALF + 1.3 && Math.abs(j.z - z) < DIVIDE_HALF + 1.3);
    // 10.9HO — PEDRAS NAS MARGENS (cor por tipo: obsidiana na lava, gelo claro, roxo no abismo)
    const bank: { x: number; z: number; s: number; c: number }[] = [];
    for (const sg of segs) {
      for (let k = 0; k < 9; k++) {
        const sd = k % 2 ? 1 : -1;
        const along = (Math.random() - 0.5) * SEG;
        const off = DIVIDE_HALF + 0.15 + Math.random() * 0.9;
        const x = sg.alongX ? sg.x + along : sg.x + sd * off;
        const z = sg.alongX ? sg.z + sd * off : sg.z + along;
        // não em cima da ponte, nem dentro do outro rio no cruzamento (10.9HQ)
        if (gates.some((g) => Math.abs(g.x - x) < g.half + 2.2 && Math.abs(g.z - z) < g.half + 2.2)) continue;
        if (nearCrossing(x, z)) continue;
        bank.push({ x, z, s: 0.25 + Math.random() * 0.55, c: DIVIDE_BANK_COLOR[sg.type] });
      }
    }
    if (bank.length) {
      const rocks = new THREE.InstancedMesh(new THREE.DodecahedronGeometry(1, 0), stoneMat(0xffffff), bank.length);
      bank.forEach((b, k) => {
        _m.position.set(b.x, b.s * 0.35, b.z);
        _m.rotation.set(Math.random() * 3, Math.random() * 3, Math.random() * 3);
        _m.scale.set(b.s, b.s * 0.7, b.s); _m.updateMatrix();
        rocks.setMatrixAt(k, _m.matrix);
        rocks.setColorAt(k, _c.setHex(b.c).multiplyScalar(0.8 + Math.random() * 0.4));
      });
      rocks.instanceMatrix.needsUpdate = true;
      if (rocks.instanceColor) rocks.instanceColor.needsUpdate = true;
      rocks.castShadow = true; rocks.frustumCulled = true; rocks.computeBoundingSphere();
      root.add(rocks);
    }
    _m.rotation.set(0, 0, 0); _m.scale.set(1, 1, 1);
    this.divideSegsByRegion.set(regionId, segs.map((sg) => ({
      x0: sg.alongX ? sg.x - SEG / 2 : sg.x, z0: sg.alongX ? sg.z : sg.z - SEG / 2,
      x1: sg.alongX ? sg.x + SEG / 2 : sg.x, z1: sg.alongX ? sg.z : sg.z + SEG / 2,
      type: sg.type, a: sg.a, b: sg.b,
    })));
    // PONTES nas portas: tabuleiro de madeira + guarda-corpos + pilares de pedra na água
    const boxGeo = new THREE.BoxGeometry(1, 1, 1);
    const deck = new THREE.InstancedMesh(boxGeo, woodMat(), Math.max(1, gates.length));
    const rails = new THREE.InstancedMesh(boxGeo, stoneMat(0x4a4358), Math.max(1, gates.length * 2));
    const piers = new THREE.InstancedMesh(boxGeo, stoneMat(0x3a3548), Math.max(1, gates.length * 2));
    deck.count = gates.length; rails.count = gates.length * 2; piers.count = gates.length * 2;
    gates.forEach((g, k) => {
      const span = DIVIDE_HALF * 2 + 2.4, wid = g.half * 2 + 0.6;
      _m.position.set(g.x, 0.12, g.z); _m.rotation.set(0, 0, 0);
      _m.scale.set(g.alongX ? span : wid, 0.16, g.alongX ? wid : span); _m.updateMatrix();
      deck.setMatrixAt(k, _m.matrix);
      for (const sd of [-1, 1]) {
        const rx = g.alongX ? g.x : g.x + sd * (g.half + 0.2), rz = g.alongX ? g.z + sd * (g.half + 0.2) : g.z;
        _m.position.set(rx, 0.48, rz); _m.scale.set(g.alongX ? span : 0.22, 0.56, g.alongX ? 0.22 : span); _m.updateMatrix();
        rails.setMatrixAt(k * 2 + (sd > 0 ? 1 : 0), _m.matrix);
        _m.position.set(rx, -0.25, rz); _m.scale.set(1.0, 0.9, 1.0); _m.updateMatrix();
        piers.setMatrixAt(k * 2 + (sd > 0 ? 1 : 0), _m.matrix);
      }
    });
    _m.scale.set(1, 1, 1);
    deck.castShadow = true; rails.castShadow = true;
    for (const im of [deck, rails, piers]) { im.instanceMatrix.needsUpdate = true; im.frustumCulled = true; im.computeBoundingSphere(); }
    root.add(deck, rails, piers);

    // 3) painéis de energia — um InstancedMesh para as portas todas
    const panels = new THREE.InstancedMesh(this.panelGeo, this.panelMat, Math.max(1, gates.length));
    // 10.9BR — era `frustumCulled = false` (herança de quando InstancedMesh não tinha boundingSphere).
    // Agora todos os InstancedMesh do módulo ficam com culling nativo + esfera calculada (abaixo).
    // 10.6X — ESTRUTURA da porta: 2 pilares + 1 arco + 2 cristais + 1 runa por porta.
    const postCount = Math.max(1, gates.length * 2);
    const posts = new THREE.InstancedMesh(this.postGeo, this.postMat, postCount);
    const beams = new THREE.InstancedMesh(this.beamGeo, this.beamMat, Math.max(1, gates.length));
    const crystals = new THREE.InstancedMesh(this.crystalGeo, this.crystalMat, postCount);
    const runes = new THREE.InstancedMesh(this.runeGeo, this.runeMat, Math.max(1, gates.length));
    posts.castShadow = true;
    for (let i = 0; i < gates.length; i++) {
      const g = gates[i];
      g.idx = i;
      // painel de energia (mesmo comportamento antigo: cor/estado)
      _m.position.set(g.x, WALL_H * 0.45, g.z);
      _m.rotation.set(0, g.alongX ? Math.PI / 2 : 0, 0);
      _m.scale.set(g.half * 2, WALL_H * 0.9, 1);
      _m.updateMatrix();
      panels.setMatrixAt(i, _m.matrix);
      _m.rotation.set(0, 0, 0); _m.scale.set(1, 1, 1);
      // pilares dos dois lados do vão
      const postH = WALL_H * 1.2;
      const off = g.half + 0.5;
      const p1x = g.alongX ? g.x : g.x - off;
      const p1z = g.alongX ? g.z - off : g.z;
      const p2x = g.alongX ? g.x : g.x + off;
      const p2z = g.alongX ? g.z + off : g.z;
      _m.position.set(p1x, postH / 2 - 0.3, p1z);
      _m.scale.set(1.1, postH, 1.1); _m.updateMatrix();
      posts.setMatrixAt(i * 2, _m.matrix);
      _m.position.set(p2x, postH / 2 - 0.3, p2z);
      _m.updateMatrix();
      posts.setMatrixAt(i * 2 + 1, _m.matrix);
      // arco/beam no topo (conecta os dois pilares)
      const beamLen = g.half * 2 + 2.2;
      _m.position.set(g.x, postH - 0.1, g.z);
      _m.scale.set(g.alongX ? 1.4 : beamLen, 0.7, g.alongX ? beamLen : 1.4);
      _m.updateMatrix();
      beams.setMatrixAt(i, _m.matrix);
      _m.scale.set(1, 1, 1);
      // cristais nos topos dos pilares (cor definida no refresh)
      const crystalY = postH + 0.35;
      _m.position.set(p1x, crystalY, p1z); _m.updateMatrix();
      crystals.setMatrixAt(i * 2, _m.matrix);
      _m.position.set(p2x, crystalY, p2z); _m.updateMatrix();
      crystals.setMatrixAt(i * 2 + 1, _m.matrix);
      // runa no chão (círculo grande sob a porta)
      _m.position.set(g.x, 0.02, g.z);
      _m.rotation.set(-Math.PI / 2, 0, 0);
      _m.scale.set(1, 1, 1);
      _m.updateMatrix();
      runes.setMatrixAt(i, _m.matrix);
      _m.rotation.set(0, 0, 0);
    }
    for (const im of [panels, posts, beams, crystals, runes]) {
      im.instanceMatrix.needsUpdate = true;
      im.frustumCulled = true;
      im.computeBoundingSphere(); // 10.9BR
    }
    root.add(posts, beams, crystals, runes, panels);
    this.panels.set(regionId, panels);
    this.crystals.set(regionId, crystals);
    this.runes.set(regionId, runes);

    this.built.set(regionId, root);
    this.gatesByRegion.set(regionId, gates);
    this.linesByRegion.set(regionId, {
      x: [...linesX].filter(inner),
      z: [...linesZ].filter(inner),
    });
    this.group.add(root);
    for (const [rid, gr] of this.built) gr.visible = rid === regionId;
  }

  /** 10.7V — atualiza o nível/Profundidade do Caçador e reavalia as portas (trava de nível). */
  setUnlockContext(level: number, depth: number): void {
    this.unlockLevel = Math.max(1, Math.floor(level));
    this.unlockDepth = Math.max(1, Math.floor(depth));
    this.refresh();
  }

  /** Recalcula quais portas estão liberadas (chamar quando um território é conquistado). */
  refresh(): void {
    const defs = this.terrs.map((t) => t.def);
    const panels = this.panels.get(this.regionId);
    const crystals = this.crystals.get(this.regionId);
    const runes = this.runes.get(this.regionId);
    const lv = this.unlockLevel;
    const dp = this.unlockDepth;
    for (const g of this.gates) {
      // a porta abre quando os DOIS lados já estão liberados para entrar (missão + nível)
      const open = this.progress.isOpen(g.a, defs, lv, dp) && this.progress.isOpen(g.b, defs, lv, dp);
      g.open = open;
      // 10.8B: trancado = vermelho FORTE (barreira sólida); aberto = quase preto (some no additive)
      if (panels) panels.setColorAt(g.idx, open ? _c.setRGB(0.01, 0.03, 0.02) : _c.setRGB(1.0, 0.14, 0.2));
      // 10.6X — cristais e runa: verde quando aberto, vermelho pulsante quando trancado
      if (crystals) {
        const cCol = open ? _c.setRGB(0.35, 1.0, 0.5) : _c.setRGB(1.0, 0.3, 0.4);
        crystals.setColorAt(g.idx * 2, cCol);
        crystals.setColorAt(g.idx * 2 + 1, cCol);
      }
      if (runes) runes.setColorAt(g.idx, open ? _c.setRGB(0.2, 0.9, 0.4) : _c.setRGB(1.0, 0.35, 0.42));
    }
    if (panels?.instanceColor) panels.instanceColor.needsUpdate = true;
    if (crystals?.instanceColor) crystals.instanceColor.needsUpdate = true;
    if (runes?.instanceColor) runes.instanceColor.needsUpdate = true;
  }

  /** Pulsação suave dos cristais/painéis das portas trancadas. */
  update(dt: number): void {
    this.time += dt;
    tickDivides(this.time); // 10.9HN — água/lava/abismo animados
    const panels = this.panels.get(this.regionId);
    if (!panels) return;
    // pulso na opacidade do painel — só afeta o CLONE deste módulo (todas as portas juntas).
    // 10.8B: base mais alta → porta trancada lê como PAREDE de energia sólida, não um véu.
    const pulse = 0.78 + Math.sin(this.time * 2.6) * 0.12;
    this.panelMat.opacity = pulse;
  }

  /** Território sob um ponto (para o Game contar abates e mostrar o nome). */
  territoryAt(x: number, z: number): Territory | null {
    for (const t of this.terrs) {
      if (Math.abs(x - t.cx) <= t.halfW && Math.abs(z - t.cz) <= t.halfH) return t;
    }
    return null;
  }

  /**
   * 10.6N — COLISÃO DO MURO. Bug reportado: dava para atravessar a parede a pé. Os blocos
   * eram só visuais — a única coisa que barrava era o painel da porta trancada. Como o muro
   * corre exatamente sobre as linhas da grade, a colisão é geométrica e exata:
   *
   *   está encostando numa linha de muro?  →  só passa se estiver DENTRO de um vão ABERTO.
   *
   * Isso cobre parede e porta na mesma regra, funciona nas 3 regiões e não depende do grid
   * de buckets (que é montado uma vez e não saberia reagir a uma porta abrindo).
   */
  block(pos: THREE.Vector3, radius: number): void {
    const reach = DIVIDE_HALF + radius; // 10.9HN — bloqueia na MARGEM do rio
    // 10.8B — COLISÃO VARRIDA (anti-tunelamento). Antes só checava a posição ATUAL: num frame
    // com lag (FPS baixo) o passo pula a espessura do muro e o Caçador atravessava a porta
    // trancada. Agora, se ele CRUZOU a linha do muro entre o frame anterior e este, é barrado
    // mesmo já estando do outro lado. Teleporte (rift/respawn, >20m) NÃO conta como cruzamento.
    const px = this.lastX;
    const pz = this.lastZ;
    const dxm = pos.x - px;
    const dzm = pos.z - pz;
    const sweep = Number.isFinite(px) && Number.isFinite(pz) && dxm * dxm + dzm * dzm <= 400;
    // linhas verticais (muro corre em Z, atravessa-se no eixo X)
    for (const lx of this.lines.x) {
      const d = pos.x - lx;
      const crossed = sweep && (px - lx) * (pos.x - lx) < 0;
      if (Math.abs(d) >= reach && !crossed) continue;
      const z = crossed && Math.abs(pos.x - px) > 1e-4 ? pz + (pos.z - pz) * ((lx - px) / (pos.x - px)) : pos.z;
      if (this.throughOpenGate(true, lx, z, radius)) continue;
      const side = crossed ? Math.sign(px - lx) || 1 : d !== 0 ? Math.sign(d) : 1;
      pos.x = lx + side * reach;
    }
    // linhas horizontais (muro corre em X, atravessa-se no eixo Z)
    for (const lz of this.lines.z) {
      const d = pos.z - lz;
      const crossed = sweep && (pz - lz) * (pos.z - lz) < 0;
      if (Math.abs(d) >= reach && !crossed) continue;
      const x = crossed && Math.abs(pos.z - pz) > 1e-4 ? px + (pos.x - px) * ((lz - pz) / (pos.z - pz)) : pos.x;
      if (this.throughOpenGate(false, lz, x, radius)) continue;
      const side = crossed ? Math.sign(pz - lz) || 1 : d !== 0 ? Math.sign(d) : 1;
      pos.z = lz + side * reach;
    }
    this.lastX = pos.x;
    this.lastZ = pos.z;
  }

  /**
   * 10.9R — COLISÃO DO MURO por-entidade (INIMIGOS). Sem sweep/estado (o block() do player usa
   * lastX/lastZ, que seria corrompido se vários inimigos chamassem). Empurra pra fora se estiver
   * encostando numa linha de muro FECHADA. Inimigos são pequenos/lentos → sem risco de túnel.
   */
  blockStatic(pos: THREE.Vector3, radius: number): void {
    const reach = DIVIDE_HALF + radius; // 10.9HN — bloqueia na MARGEM do rio
    for (const lx of this.lines.x) {
      const d = pos.x - lx;
      if (Math.abs(d) >= reach) continue;
      if (this.throughOpenGate(true, lx, pos.z, radius)) continue;
      pos.x = lx + (d !== 0 ? Math.sign(d) : 1) * reach;
    }
    for (const lz of this.lines.z) {
      const d = pos.z - lz;
      if (Math.abs(d) >= reach) continue;
      if (this.throughOpenGate(false, lz, pos.x, radius)) continue;
      pos.z = lz + (d !== 0 ? Math.sign(d) : 1) * reach;
    }
  }

  /** Existe um vão ABERTO nesta linha, na altura em que o Caçador está tentando passar? */
  private throughOpenGate(vertical: boolean, line: number, along: number, radius: number): boolean {
    for (const g of this.gates) {
      if (!g.open) continue;
      if (g.alongX !== vertical) continue;
      const gl = vertical ? g.x : g.z;
      if (Math.abs(gl - line) > 0.6) continue;
      const ga = vertical ? g.z : g.x;
      if (Math.abs(along - ga) <= g.half - radius) return true;
    }
    return false;
  }


  /** 10.6M — território ainda TRANCADO do outro lado da porta mais próxima (para o HUD). */
  /** 10.9EK — portas da regiao ativa pro MINIMAPA (posicao, orientacao do vao e se esta aberta). */
  mapGates(): { x: number; z: number; alongX: boolean; half: number; open: boolean; a: string; b: string }[] {
    return this.gates.map((g) => ({ x: g.x, z: g.z, alongX: g.alongX, half: g.half, open: g.open, a: g.a.id, b: g.b.id })); // 10.9EL: + territorios dos dois lados
  }

  lockedNeighbour(x: number, z: number, maxDist = 10): TerritoryDef | null {
    const g = this.nearestLocked(x, z, maxDist);
    if (!g) return null;
    const defs = this.terrs.map((t) => t.def);
    // o lado que ainda não foi liberado é o que precisa de missão/nível
    return this.progress.isOpen(g.a, defs, this.unlockLevel, this.unlockDepth) ? g.a : g.b;
  }

  /** A porta mais próxima do Caçador (para o HUD dizer o que falta). */
  nearestLocked(x: number, z: number, maxDist = 9): Gate | null {
    let best: Gate | null = null;
    let bd = maxDist * maxDist;
    for (const g of this.gates) {
      if (g.open) continue;
      const d = (g.x - x) ** 2 + (g.z - z) ** 2;
      if (d < bd) {
        bd = d;
        best = g;
      }
    }
    return best;
  }

  /**
   * 10.6O — tem parede entre dois pontos? Checa se o segmento (ax,az)→(bx,bz) cruza
   * alguma linha de muro sem ter um vão ABERTO no ponto de cruzamento.
   */
  blocksSegment(ax: number, az: number, bx: number, bz: number): boolean {
    for (const lx of this.lines.x) {
      if ((ax - lx) * (bx - lx) > 0) continue;
      const dx = bx - ax;
      if (Math.abs(dx) < 1e-6) continue;
      const t = (lx - ax) / dx;
      const zCross = az + (bz - az) * t;
      if (!this.gateOpenAt(true, lx, zCross)) return true;
    }
    for (const lz of this.lines.z) {
      if ((az - lz) * (bz - lz) > 0) continue;
      const dz = bz - az;
      if (Math.abs(dz) < 1e-6) continue;
      const t = (lz - az) / dz;
      const xCross = ax + (bx - ax) * t;
      if (!this.gateOpenAt(false, lz, xCross)) return true;
    }
    return false;
  }

  private gateOpenAt(vertical: boolean, line: number, along: number): boolean {
    for (const g of this.gates) {
      if (!g.open) continue;
      if (g.alongX !== vertical) continue;
      const gl = vertical ? g.x : g.z;
      if (Math.abs(gl - line) > 0.6) continue;
      const ga = vertical ? g.z : g.x;
      if (Math.abs(along - ga) <= g.half) return true;
    }
    return false;
  }

  setVisible(v: boolean): void {
    this.group.visible = v;
  }
}

/**
 * 10.6O — LINHA DE VISÃO ATRAVÉS DE MUROS. Antes os ataques (melee, projéteis,
 * bolts de inimigo) atravessavam os muros como se não existissem: a colisão só
 * barrava o CORPO do Caçador. Agora todos os sistemas de combate perguntam aqui
 * se tem parede no caminho antes de causar dano.
 */
let _activeGates: TerritoryGates | null = null;
export function wallBetween(ax: number, az: number, bx: number, bz: number): boolean {
  return _activeGates ? _activeGates.blocksSegment(ax, az, bx, bz) : false;
}

/** Config usada pelo TerritoryProgress (exposta aqui para ficar perto do muro). */
export const GATE_CONFIG = GameConfig.territories;
